import { Button } from "@/components/ui/button";
import { CalendarCheck, Clock, ShieldCheck, Sparkles, Store } from "lucide-react";
import { bookingUrl, saasOrigin } from "@/lib/site";
import BrandLogo from "@/components/BrandLogo";

const HERO = "/hero-salon.png";
const SPA1 = "https://images.unsplash.com/photo-1731336479432-3eb5fdb3ab1c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBzcGElMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzgwNzcwNzF8MA&ixlib=rb-4.1.0&q=85";
const SALON1 = "https://images.unsplash.com/photo-1722935408489-2bf93349c8cb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA7MDB8MHwxfHNlYXJjaHwyfHxpbmRpYW4lMjBzYWxvbiUyMGhhaXJzdHlsaXN0fGVufDB8fHx8MTc3ODA3NzA3MXww&ixlib=rb-4.1.0&q=85";

const steps = [
    { i: Sparkles, t: "Choose your service", d: "Browse the salon's menu — haircuts, facials, spa, nails and more." },
    { i: Clock, t: "Pick a time", d: "See live availability and grab a slot that suits you." },
    { i: CalendarCheck, t: "Confirm in seconds", d: "Add your details and you're booked. No app, no account, no calls." },
];

export default function BookingHome() {
    const demo = bookingUrl("demo-salon");

    return (
        <div className="min-h-screen bg-background">
            {/* Top nav */}
            <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
                <div className="mx-auto max-w-7xl px-6 h-16 flex items-center justify-between">
                    <a href="/">
                        <BrandLogo />
                    </a>
                    <a
                        href={`${saasOrigin()}/register`}
                        className="text-sm text-foreground/70 hover:text-primary flex items-center gap-1.5"
                        data-testid="list-your-salon"
                    >
                        <Store className="w-4 h-4" />
                        List your salon
                    </a>
                </div>
            </header>

            {/* Hero */}
            <section className="relative overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center opacity-30"
                    style={{ backgroundImage: `url(${HERO})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/80 to-background" />
                <div className="relative mx-auto max-w-7xl px-6 pt-20 pb-28 grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-7">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/15 border border-accent/40 text-xs text-secondary mb-6">
                            <Sparkles className="w-3.5 h-3.5 text-accent" />
                            Salon &amp; spa bookings, made effortless
                        </div>
                        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-medium leading-[1.05] text-secondary tracking-tight">
                            Your favourite salon,<br />
                            <span className="text-primary italic">booked in seconds.</span>
                        </h1>
                        <p className="mt-6 text-lg text-foreground/75 max-w-xl">
                            Got a booking link from your salon? Open it to pick a service and time.
                            No app to download, no account to create — just choose, tap, and you're set.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <a href={demo} data-testid="booking-home-demo">
                                <Button size="lg" className="bg-primary hover:bg-[#A33923] text-base">
                                    Try a live booking
                                </Button>
                            </a>
                        </div>
                        <div className="mt-8 flex items-center gap-6 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1.5">
                                <ShieldCheck className="w-4 h-4 text-accent" />
                                Secure &amp; private
                            </div>
                            <div className="flex items-center gap-1.5">
                                <Clock className="w-4 h-4 text-accent" />
                                Book 24×7
                            </div>
                        </div>
                    </div>

                    <div className="md:col-span-5 grid grid-cols-2 gap-4">
                        <div className="aspect-[3/4] overflow-hidden rounded-xl border border-border bg-muted">
                            <img src={SPA1} alt="Luxury spa" className="w-full h-full object-cover" />
                        </div>
                        <div className="space-y-4 mt-12">
                            <div className="aspect-square overflow-hidden rounded-xl border border-border">
                                <img src={SALON1} alt="Indian salon" className="w-full h-full object-cover" />
                            </div>
                            <div className="rounded-xl border border-border bg-card p-5">
                                <div className="text-xs text-muted-foreground">Next available</div>
                                <div className="font-serif text-3xl text-secondary mt-1">Today</div>
                                <div className="text-xs text-primary mt-1">Instant confirmation</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section className="mx-auto max-w-7xl px-6 py-24">
                <div className="max-w-2xl">
                    <p className="text-sm uppercase tracking-widest text-primary mb-3">How it works</p>
                    <h2 className="font-serif text-4xl sm:text-5xl text-secondary tracking-tight">
                        Three taps to your next appointment.
                    </h2>
                </div>
                <div className="mt-12 grid md:grid-cols-3 gap-6">
                    {steps.map(({ i: Icon, t, d }) => (
                        <div
                            key={t}
                            className="group rounded-xl border border-border bg-card p-6 hover:-translate-y-1 hover:border-primary/40 transition-all duration-300"
                        >
                            <div className="w-10 h-10 rounded-md bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                                <Icon className="w-5 h-5" />
                            </div>
                            <h3 className="font-serif text-2xl mt-4 text-secondary">{t}</h3>
                            <p className="mt-2 text-sm text-foreground/70 leading-relaxed">{d}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Salon owner strip */}
            <section className="bg-secondary text-secondary-foreground">
                <div className="mx-auto max-w-7xl px-6 py-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
                    <div className="max-w-xl">
                        <p className="text-sm uppercase tracking-widest text-accent mb-3">Own a salon or spa?</p>
                        <h2 className="font-serif text-4xl tracking-tight">
                            Get your own booking page in 15 minutes.
                        </h2>
                        <p className="text-secondary-foreground/75 mt-3">
                            Bookings, billing, memberships and reports — run everything on WebSalon and
                            share a booking link just like this one.
                        </p>
                    </div>
                    <a href={`${saasOrigin()}/register`} data-testid="owner-cta">
                        <Button size="lg" className="bg-accent text-secondary hover:bg-accent/90 text-base">
                            Start free on WebSalon
                        </Button>
                    </a>
                </div>
            </section>

            <footer className="border-t border-border py-10 text-center text-sm text-muted-foreground">
                <BrandLogo className="justify-center mb-2" textClass="font-serif text-xl text-secondary" />
                Powered by <a href={saasOrigin()} className="text-primary hover:underline">WebSalon</a> · Built in India for India
            </footer>
        </div>
    );
}
